'use strict';
const {sdkTextElement, sdkMessageElement, localizeSdkElement} = require('../i18n/sdk-text');


// Presentation only: every receipt is read through the existing validated pack session.
// Remembering a notice must never interfere with the opening request's saved idempotency key.
const seen = new Set();
const delays = [3000, 5000, 10000, 15000, 30000];

function complete(pack) {
  return !!(pack && pack.status === 'OPENED' && pack.deliveryStatus === 'GRANTED' &&
    pack.grant && typeof pack.grant.grantId === 'string' && pack.grant.grantId &&
    Array.isArray(pack.grant.components) && pack.grant.components.length &&
    pack.grant.components.every(component => component.status === 'GRANTED'));
}

function createDeliveryNotice({scopeKey, packId, storage, reconcile, onNotice, canNotify = () => true,
  document: doc, setTimer = setTimeout, clearTimer = clearTimeout}) {
  let disposed = false, timer = null, inFlight = false, attempts = 0, snapshot = null, revision = 0;
  const visible = () => !doc || !doc.hidden;
  const currentPack = () => snapshot && snapshot.pack && snapshot.pack.packId === packId ? snapshot.pack : null;
  const waiting = () => {
    const pack = currentPack();
    return pack && pack.status === 'OPENED' && ['PENDING', 'PARTIAL'].includes(pack.deliveryStatus);
  };
  const cancel = () => {if (timer !== null) clearTimer(timer); timer = null;};

  function notify() {
    const pack = currentPack();
    if (disposed || !visible() || !canNotify() || !complete(pack)) return;
    // The runtime's storage adapter is owner-scoped to this prefix. This dedicated
    // suffix never overlaps the existing ":open" or ":reveal" request-ID records.
    const key = 'op-card-open-v1:' + scopeKey + ':' + packId + ':delivery-notice:' + pack.grant.grantId;
    let acknowledged = seen.has(key);
    try {acknowledged = acknowledged || storage.getItem(key) === 'shown';} catch (_) {}
    if (acknowledged) return;
    seen.add(key);
    try {onNotice(pack);} catch (_) {seen.delete(key); return;}
    try {storage.setItem(key, 'shown');} catch (_) {}
  }

  function schedule(immediate = false) {
    cancel();
    if (disposed || inFlight || !visible() || !waiting() || snapshot.busy) return;
    timer = setTimer(async () => {
      timer = null;
      if (disposed || inFlight || !visible() || !waiting() || snapshot.busy) return;
      inFlight = true;
      const before = revision;
      try {
        const result = await reconcile();
        if (!disposed && revision === before && result) snapshot = result;
      } catch (_) {
        // The existing session owns error presentation. A status read cannot issue a reward.
      } finally {
        inFlight = false;
        if (!disposed) {attempts++; notify(); schedule();}
      }
    }, immediate ? 0 : delays[Math.min(attempts, delays.length - 1)]);
  }

  function visibility() {if (!visible()) cancel(); else {notify(); schedule(true);}}
  if (doc && doc.addEventListener) doc.addEventListener('visibilitychange', visibility);
  return {
    update(value) {if (disposed) return; revision++; snapshot = value; notify(); schedule();},
    refresh() {notify();},
    dispose() {disposed = true; cancel(); if (doc && doc.removeEventListener) doc.removeEventListener('visibilitychange', visibility);}
  };
}

function createRewardDeliveredDialog(React) {
  const h = React.createElement;
  return class RewardDeliveredDialog extends React.Component {
    constructor(props) {
      super(props);
      this.keydown = event => {
        if (event.key === 'Escape') {event.preventDefault(); event.stopPropagation(); this.props.onClose();}
        if (event.key === 'Tab') {event.preventDefault(); if (this.confirm) this.confirm.focus();}
      };
      this.keepFocus = event => {if (this.dialog && !this.dialog.contains(event.target) && this.confirm) this.confirm.focus();};
    }
    componentDidMount() {
      this.doc = this.dialog && this.dialog.ownerDocument;
      this.previousFocus = this.doc && this.doc.activeElement;
      if (this.doc) this.doc.addEventListener('focusin', this.keepFocus);
      if (this.confirm) this.confirm.focus({preventScroll:true});
    }
    componentWillUnmount() {
      if (this.doc) this.doc.removeEventListener('focusin', this.keepFocus);
      if (this.previousFocus && this.previousFocus.isConnected && typeof this.previousFocus.focus === 'function') this.previousFocus.focus({preventScroll:true});
    }
    render() {
      return h('div', {className:'opcards-delivery-backdrop', onKeyDown:this.keydown},
        h('section', {className:'opcards-delivery-dialog', role:'dialog', 'aria-modal':true,
          'aria-labelledby':'opcards-delivery-title', 'aria-describedby':'opcards-delivery-description', ref:node => this.dialog = node},
          h('span', {className:'opcards-delivery-check', 'aria-hidden':true}, '✓'),
          h('p', {className:'opcards-delivery-eyebrow'}, sdkTextElement('獎勵已到帳')),
          h('h2', {id:'opcards-delivery-title'}, sdkTextElement('本卡獎勵已送達')),
          h('p', {className:'opcards-delivery-reward'}, this.props.reward),
          h('p', {id:'opcards-delivery-description'}, sdkTextElement('本張卡片的獎勵已全部發送完成。')),
          h('button', {type:'button', className:'opcards-primary', ref:node => this.confirm = node, onClick:this.props.onClose}, sdkTextElement('知道了'))));
    }
  };
}

module.exports = {createDeliveryNotice, createRewardDeliveredDialog};
